/**
 * In-context panels for the assisted passage-plan steps. The conductor strip
 * (in PlanningSheet) shows where we are; this renders the guided UI for the step
 * we're on. The steps drive the SAME tools the free-form buckets use, so a route
 * plotted here is an ordinary route, editable anywhere.
 *
 * Built so far: the destination prompt and the manual route wizard. The check,
 * pilotage and follow steps lean on their buckets + the strip hint for now.
 */
import { useMemo, type ReactNode } from 'react';
import { useChartPlotterStore } from '@/store';
import { providerHasDepthData, NO_DEPTH_ON_SOURCE } from '@/chart-sources';
import { useShellStore } from '@/store/shell';
import { useSettingsStore } from '@/store/settings';
import { useEnvStore } from '@/store/environment';
import { useRoutingStore, nextWaypointId, nextRouteIdFn } from '@/store/routing';
import { useRoutePlanStore, buildPassageRoute } from '@/store/routePlan';
import { usePilotageStore } from '@/store/pilotage';
import { usePassagePlanStore, type PlanStepId } from '@/store/passagePlan';
import { formatLatLon, rhumbDistanceNm } from '@/nav';
import { recommendDeparture, recommendForArrival, simulatePassage, pilotageSectionTitle, findRouteTidalGates, type PilotageSection } from '@/passage';
import { streamAtPosition } from '@/tides';

function fmtPos(p: [number, number]): string {
  return `${formatLatLon(p[1], 'lat')} ${formatLatLon(p[0], 'lon')}`;
}

/** A subtle grouping box, matching the conductor strip's look. */
function StepBox(props: { children: ReactNode }) {
  return (
    <div style={{ border: '1px solid rgba(127,127,127,0.25)', borderRadius: 10, padding: 10, marginBottom: 10 }}>
      {props.children}
    </div>
  );
}

function DestinationStep() {
  const destination = useRoutingStore((s) => s.destination);
  const clearDestination = useRoutingStore((s) => s.clearDestination);
  return (
    <StepBox>
      {destination ? (
        <>
          <p className="sub">Destination set at {fmtPos(destination.position)}. Tap Next to plot a route to it.</p>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" onClick={() => clearDestination()}>
              Clear destination
            </button>
          </div>
        </>
      ) : (
        <p className="sub">
          Tap the chart and choose “Set as destination”, then come back here and tap Next.
        </p>
      )}
    </StepBox>
  );
}

/**
 * The stops/via list with a dwell editor. A via (dwell 0) just makes the route
 * pass through; a stop (dwell > 0) adds hours that shift the later ETAs. Shown in
 * the Route step; reads its own store slices so it works in either branch.
 */
function ViaEditor({ onReplot }: { onReplot?: () => void }) {
  const vias = useRoutePlanStore((s) => s.vias);
  const setViaDwell = useRoutePlanStore((s) => s.setViaDwell);
  const removeVia = useRoutePlanStore((s) => s.removeVia);
  if (vias.length === 0) return null;
  return (
    <>
      <label className="acp-fieldlabel" style={{ marginTop: 8 }}>
        Stops &amp; via points ({vias.length})
      </label>
      {vias.map((v, i) => (
        <div className="acp-kv" key={i}>
          <span className="k">
            {v.dwellH > 0 ? `Stop ${i + 1}` : `Via ${i + 1}`} · {fmtPos(v.position)}
          </span>
          <span className="v" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <button
              type="button"
              className="acp-btn"
              aria-label={`Less dwell at ${i + 1}`}
              onClick={() => setViaDwell(i, Math.max(0, v.dwellH - 1))}
            >
              −
            </button>
            <span style={{ minWidth: 52, textAlign: 'center' }}>
              {v.dwellH > 0 ? `${v.dwellH} h` : 'pass'}
            </span>
            <button
              type="button"
              className="acp-btn"
              aria-label={`More dwell at ${i + 1}`}
              onClick={() => setViaDwell(i, v.dwellH + 1)}
            >
              +
            </button>
            <button
              type="button"
              className="acp-btn"
              aria-label={`Remove ${i + 1}`}
              onClick={() => removeVia(i)}
            >
              ✕
            </button>
          </span>
        </div>
      ))}
      {onReplot && (
        <div className="acp-btn-row">
          <button type="button" className="acp-btn full" onClick={onReplot}>
            Replot through these
          </button>
        </div>
      )}
      <p className="acp-note">
        A stop adds its hours to the passage and pushes the later ETAs back; a via just makes the
        route pass through. Drop the dwell to 0 (−) to turn a stop into a plain via.
      </p>
    </>
  );
}

function RouteStep() {
  const destination = useRoutingStore((s) => s.destination);
  const routes = useRoutingStore((s) => s.routes);
  const ownship = useChartPlotterStore((s) => s.ownship.position);
  const vias = useRoutePlanStore((s) => s.vias);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);

  const startMode = useRoutePlanStore((s) => s.startMode);
  const startPoint = useRoutePlanStore((s) => s.startPoint);
  const plotMode = useRoutePlanStore((s) => s.plotMode);
  const plannedRouteId = useRoutePlanStore((s) => s.plannedRouteId);
  const setStartMode = useRoutePlanStore((s) => s.setStartMode);
  const setPlotMode = useRoutePlanStore((s) => s.setPlotMode);
  const autoStatus = useRoutePlanStore((s) => s.autoStatus);
  const autoError = useRoutePlanStore((s) => s.autoError);
  const chartProviderId = useChartPlotterStore((s) => s.chartProviderId);
  const polarEnabled = useSettingsStore((s) => s.polarEnabled);
  const draughtM = useSettingsStore((s) => s.draughtM);
  const cruiseKn = useSettingsStore((s) => s.cruiseSpeedKn);
  const departMs = useEnvStore((s) => s.streamTimeMs);
  const planBasis = useRoutePlanStore((s) => s.planBasis);
  const setPlanBasis = useRoutePlanStore((s) => s.setPlanBasis);
  const targetArriveMs = useRoutePlanStore((s) => s.targetArriveMs);
  const setTargetArriveMs = useRoutePlanStore((s) => s.setTargetArriveMs);

  const plannedRoute = plannedRouteId ? routes.find((r) => r.id === plannedRouteId) : undefined;

  // Rebuild the planned route to thread through the current vias/stops, reading
  // everything fresh from the stores so it works whether or not a route exists yet.
  function replotWithVias() {
    const rp = useRoutePlanStore.getState();
    const dest = useRoutingStore.getState().destination;
    const own = useChartPlotterStore.getState().ownship.position;
    const s: [number, number] | null = rp.startMode === 'current' ? own ?? null : rp.startPoint;
    if (!dest || !s) {
      showToast('Need a start and destination to replot');
      return;
    }
    const rs = useRoutingStore.getState();
    if (rp.plannedRouteId) rs.removeRoute(rp.plannedRouteId);
    const id = nextRouteIdFn();
    const route = buildPassageRoute({
      id,
      start: s,
      destination: dest.position,
      vias: rp.vias.map((v) => ({ position: v.position, dwellH: v.dwellH })),
      makeWpId: nextWaypointId,
    });
    rs.upsertRoute(route);
    rs.setActiveRoute(id);
    rp.setPlannedRouteId(id);
    showToast('Replotted through your stops');
  }

  // Already plotted: now it's about shaping the line, not asking questions.
  if (plannedRoute) {
    return (
      <StepBox>
        <p className="sub">
          Route plotted. Zoom in and shape it if you like: long-press the line to add a
          waypoint, drag a waypoint to move it. When it looks right, tap Next to check it at high zoom.
        </p>
        <label className="acp-fieldlabel">Plan by</label>
        <div className="acp-seg">
          <button type="button" className={planBasis === 'depart' ? 'on' : ''} onClick={() => setPlanBasis('depart')}>
            Depart at
          </button>
          <button type="button" className={planBasis === 'arrive' ? 'on' : ''} onClick={() => setPlanBasis('arrive')}>
            Arrive by
          </button>
        </div>
        {planBasis === 'arrive' && (
          <>
            <label className="acp-fieldlabel" style={{ marginTop: 6 }}>Arrive by (UT)</label>
            <input
              type="time"
              className="acp-text-input"
              value={targetArriveMs ? msToHHMM(targetArriveMs) : ''}
              aria-label="Target arrival time"
              onChange={(e) => setTargetArriveMs(hhmmToMs(e.target.value, departMs))}
            />
            <p className="acp-note">
              Over this route: the fair-tide departure to make your target, the latest you could leave, and
              the full per-leg timing in the Follow step. Suggested and tide-aware — check at high zoom.
            </p>
          </>
        )}
        {planBasis === 'arrive' &&
          targetArriveMs != null &&
          (() => {
            const w = plannedRoute.waypoints.map((wp) => wp.position);
            const a = recommendForArrival(w, departMs, targetArriveMs, 30, cruiseKn, targetArriveMs);
            if (!a) return null;
            return (
              <p className="acp-note">
                {a.feasible
                  ? `To arrive by ${fmtClock(targetArriveMs)}: leave by ${fmtClock(a.best!.departMs)} (fair tide, ${fmtDur(a.best!.durationMin)}); latest ${fmtClock(a.latest!.departMs)}. Full breakdown in Follow.`
                  : a.allFoul
                    ? `Foul tide throughout — can’t make ${fmtClock(targetArriveMs)}.`
                    : a.earliest
                      ? `Can’t make ${fmtClock(targetArriveMs)} — soonest arrival ${fmtClock(a.earliest.arriveMs)} (leave ${fmtClock(a.earliest.departMs)}).`
                      : `No departure meets ${fmtClock(targetArriveMs)}.`}
              </p>
            );
          })()}
        <ViaEditor onReplot={replotWithVias} />
        <div className="acp-btn-row">
          <button
            type="button"
            className="acp-btn"
            onClick={() => {
              useRoutingStore.getState().removeRoute(plannedRoute.id);
              useRoutePlanStore.getState().setPlannedRouteId(null);
              showToast('Cleared — plot again');
            }}
          >
            Start over
          </button>
        </div>
      </StepBox>
    );
  }

  if (!destination) {
    return (
      <StepBox>
        <p className="sub">No destination yet. Go Back a step, set one, then return here to plot the route.</p>
      </StepBox>
    );
  }

  const start: [number, number] | null = startMode === 'current' ? ownship ?? null : startPoint;
  const canPlot = !!destination && !!start && plotMode === 'manual';

  function plot() {
    if (!destination || !start) return;
    const id = nextRouteIdFn();
    const route = buildPassageRoute({
      id,
      start,
      destination: destination.position,
      vias: vias.map((v) => ({ position: v.position, dwellH: v.dwellH })),
      makeWpId: nextWaypointId,
    });
    const rs = useRoutingStore.getState();
    rs.upsertRoute(route);
    rs.setActiveRoute(id);
    useRoutePlanStore.getState().setPlannedRouteId(id);
    closeSheet();
    showToast(
      vias.length
        ? 'Plotted through your stops. Long-press the line to shape it; then check it.'
        : 'Straight line plotted. Long-press the line to add waypoints, drag to shape; then check it.',
    );
  }

  const isVector = chartProviderId === 'vector';
  const canAuto = !!destination && !!start && isVector;
  function autoPlot() {
    if (!destination || !start) return;
    useRoutePlanStore.getState().requestAutoRoute(start, destination.position);
  }

  return (
    <StepBox>
      <label className="acp-fieldlabel">Start from</label>
      <div className="acp-seg">
        <button type="button" className={startMode === 'current' ? 'on' : ''} onClick={() => setStartMode('current')}>
          The boat
        </button>
        <button type="button" className={startMode === 'point' ? 'on' : ''} onClick={() => setStartMode('point')}>
          Another point
        </button>
      </div>
      {startMode === 'current' && !ownship && (
        <p className="acp-note">No fix yet, so we can’t start from the boat. Pick another point instead.</p>
      )}
      {startMode === 'point' && !startPoint && (
        <p className="acp-note">Tap the chart and choose “Set as start”, then come back here.</p>
      )}

      <label className="acp-fieldlabel" style={{ marginTop: 8 }}>Plot</label>
      <div className="acp-seg">
        <button type="button" className={plotMode === 'manual' ? 'on' : ''} onClick={() => setPlotMode('manual')}>
          Manual
        </button>
        <button
          type="button"
          className={plotMode === 'auto' ? 'on' : ''}
          disabled={!isVector}
          onClick={() => setPlotMode('auto')}
        >
          Automatic
        </button>
      </div>
      {!isVector && (
        <p className="acp-note">Auto-routing needs the vector chart. Switch the chart source to RYA vector to use it.</p>
      )}

      {plotMode === 'manual' ? (
        <>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn primary full" onClick={plot} disabled={!canPlot}>
              {vias.length ? 'Plot through the stops to the destination' : 'Plot a straight line to the destination'}
            </button>
          </div>
          <ViaEditor />
          <p className="acp-note">
            Manual gives you the line first (threaded through any stops), then you shape it by
            long-pressing the line to add waypoints and dragging them. Tap the chart to add a “Go via”
            point or a “Stop here”.
          </p>
        </>
      ) : (
        <>
          <label className="acp-fieldlabel">Plan by</label>
          <div className="acp-seg">
            <button type="button" className={planBasis === 'depart' ? 'on' : ''} onClick={() => setPlanBasis('depart')}>
              Depart at
            </button>
            <button type="button" className={planBasis === 'arrive' ? 'on' : ''} onClick={() => setPlanBasis('arrive')}>
              Arrive by
            </button>
          </div>
          {planBasis === 'arrive' && (
            <>
              <label className="acp-fieldlabel" style={{ marginTop: 6 }}>Arrive by (UT)</label>
              <input
                type="time"
                className="acp-text-input"
                value={targetArriveMs ? msToHHMM(targetArriveMs) : ''}
                aria-label="Target arrival time"
                onChange={(e) => setTargetArriveMs(hhmmToMs(e.target.value, departMs))}
              />
              <p className="acp-note">
                It plots the tide-and-wind-aware route, then recommends the departure (fair tide) to arrive
                by your target. The route and its departure show after plotting and in the Follow step.
              </p>
            </>
          )}
          <label className="acp-fieldlabel" style={{ marginTop: 6 }}>It will plot with</label>
          <div className="acp-kv">
            <span className="k">Min depth</span>
            <span className="v">draught {draughtM.toFixed(1)} m + 0.5 m clearance</span>
          </div>
          <div className="acp-kv">
            <span className="k">Boat</span>
            <span className="v">{polarEnabled ? 'Sailing — tacks upwind' : 'Motoring at cruise'}</span>
          </div>
          <div className="acp-kv">
            <span className="k">Cruise speed</span>
            <span className="v">{cruiseKn.toFixed(0)} kn</span>
          </div>
          {planBasis === 'depart' ? (
            <div className="acp-kv">
              <span className="k">Departs</span>
              <span className="v">{fmtClock(departMs)} · the scrubber time</span>
            </div>
          ) : (
            <div className="acp-kv">
              <span className="k">Arrive by</span>
              <span className="v">{targetArriveMs ? fmtClock(targetArriveMs) : 'set a time above'}</span>
            </div>
          )}
          <p className="acp-note">
            These come from Settings (draught, cruise speed, sailing polar) and the time scrubber
            (departure). Adjust them first, then plot; the timing and best-departure come next.
          </p>
          <div className="acp-btn-row">
            <button
              type="button"
              className="acp-btn primary full"
              onClick={autoPlot}
              disabled={!canAuto || autoStatus === 'running'}
            >
              {autoStatus === 'running' ? 'Routing…' : 'Plot automatically'}
            </button>
          </div>
          {autoStatus === 'failed' && autoError && <p className="acp-note">{autoError}</p>}
          <p className="acp-note">
            A tide-and-wind-aware route around the charted depths, built on a coarse grid with the
            sparse diamonds — a suggested route, always check it at high zoom. {polarEnabled
              ? 'Your sailing polar is on, so it will sail and tack.'
              : 'No sailing polar set, so it motors at cruise speed (set a polar in Settings for sail routing).'}
          </p>
        </>
      )}
    </StepBox>
  );
}

const HAZARD_LABEL: Record<string, string> = {
  wreck: 'Wreck',
  obstruction: 'Obstruction',
  shallow: 'Shallow water',
  isolated_danger: 'Isolated danger',
};

function CheckStep() {
  const routeHazards = useRoutePlanStore((s) => s.routeHazards);
  // The scan reads depth off the vector ENC; a raster/tiles source has none, so
  // an empty list there means "couldn't check", not "nothing found".
  const hasDepthData = useChartPlotterStore((s) => providerHasDepthData(s.chartProviderId));
  const plannedRouteId = useRoutePlanStore((s) => s.plannedRouteId);
  const routes = useRoutingStore((s) => s.routes);
  const departMs = useEnvStore((s) => s.streamTimeMs);
  const setFocus = useShellStore((s) => s.setFocus);
  const showToast = useShellStore((s) => s.showToast);
  const tidalGates = useMemo(() => {
    const r = plannedRouteId ? routes.find((x) => x.id === plannedRouteId) : undefined;
    if (!r || r.waypoints.length < 2) return [];
    return findRouteTidalGates(r.waypoints.map((w) => w.position), departMs, streamAtPosition);
  }, [plannedRouteId, routes, departMs]);
  const route = plannedRouteId ? routes.find((r) => r.id === plannedRouteId) : undefined;
  if (!route) {
    return (
      <StepBox>
        <p className="sub">No route to check yet. Go Back and plot one first.</p>
      </StepBox>
    );
  }
  return (
    <StepBox>
      <p className="sub">
        Before you commit to this route, zoom right in and walk along every leg by eye. The scan
        below is a coarse proximity check on the training cells, not a swept clearance.
      </p>
      {!hasDepthData ? (
        <p className="acp-note" style={{ color: '#e0a03a' }}>
          {NO_DEPTH_ON_SOURCE} The hazard scan can&apos;t check this route until then. Walk every leg by
          eye, especially at the turns and close to land.
        </p>
      ) : routeHazards.length === 0 ? (
        <p className="acp-note">
          No charted hazards found within half a mile of the track. Still check by eye, especially
          at the turns and close to land.
        </p>
      ) : (
        <>
          <label className="acp-fieldlabel">Charted hazards near the track ({routeHazards.length})</label>
          {routeHazards.slice(0, 12).map((h, i) => (
            <div className="acp-kv" key={i}>
              <span className="k">{HAZARD_LABEL[h.reason] ?? h.reason}</span>
              <span className="v">
                {h.distNm.toFixed(2)} nm off · leg {h.legIndex + 1}
              </span>
            </div>
          ))}
          {routeHazards.length > 12 && <p className="acp-note">…and {routeHazards.length - 12} more.</p>}
          <p className="acp-note">
            Give these more room by shaping the route, or set a tighter cross-track limit on the leg.
            Then tap Next to make any pilotage plans.
          </p>
        </>
      )}
      <label className="acp-fieldlabel" style={{ marginTop: 10 }}>Tidal gates along the route</label>
      {tidalGates.length === 0 ? (
        <p className="acp-note">
          No leg has a hard foul tide at the planned time. Streams still turn through the passage, so
          keep an eye on the arrows as we go.
        </p>
      ) : (
        <>
          {tidalGates.map((g) => (
            <div className="acp-route-row" key={g.legIndex}>
              <div className="acp-route-sel" style={{ pointerEvents: 'none' }}>
                <b>
                  {route!.waypoints[g.legIndex].name} → {route!.waypoints[g.legIndex + 1].name}
                </b>
                <span>
                  {g.rateKn.toFixed(1)} kn foul ·{' '}
                  {g.nextWindow ? `eases ${fmtClock(g.nextWindow.startMs)}` : 'stays foul in the next 12 h'}
                </span>
              </div>
              <button
                type="button"
                className="acp-btn"
                onClick={() => {
                  setFocus(g.position, 13);
                  showToast('Centred on the gate — check the stream arrows here');
                }}
              >
                Show
              </button>
            </div>
          ))}
          <p className="acp-note">
            These legs have the tide setting hard against us at the planned time. Either wait for the
            window shown, or leave at the best departure in the Follow step so we take them fair.
          </p>
        </>
      )}
    </StepBox>
  );
}

function fmtClock(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UT`;
}
/** Parse HH:MM into the next such UTC instant at or after baseMs (rolls a day). */
function hhmmToMs(hhmm: string, baseMs: number): number | null {
  if (!/^\d{1,2}:\d{2}$/.test(hhmm)) return null;
  const [hh, mm] = hhmm.split(':').map(Number);
  if (hh > 23 || mm > 59) return null;
  const d = new Date(baseMs);
  let t = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), hh, mm);
  if (t <= baseMs) t += 24 * 3_600_000;
  return t;
}
function msToHHMM(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}
function fmtDur(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function FollowStep() {
  const plannedRouteId = useRoutePlanStore((s) => s.plannedRouteId);
  const routes = useRoutingStore((s) => s.routes);
  const vias = useRoutePlanStore((s) => s.vias);
  const cruiseKn = useSettingsStore((s) => s.cruiseSpeedKn);
  const streamTimeMs = useEnvStore((s) => s.streamTimeMs);
  const plannedDepartureMs = useRoutePlanStore((s) => s.plannedDepartureMs);
  const showToast = useShellStore((s) => s.showToast);
  const closeSheet = useShellStore((s) => s.closeSheet);

  const planBy = useRoutePlanStore((s) => s.planBasis);
  const setPlanBy = useRoutePlanStore((s) => s.setPlanBasis);
  const targetArriveMs = useRoutePlanStore((s) => s.targetArriveMs);
  const setTargetArriveMs = useRoutePlanStore((s) => s.setTargetArriveMs);

  const dwellsH = useMemo(() => {
    const r = plannedRouteId ? routes.find((x) => x.id === plannedRouteId) : undefined;
    if (!r || r.waypoints.length !== vias.length + 2) return undefined;
    return [0, ...vias.map((v) => v.dwellH), 0];
  }, [plannedRouteId, routes, vias]);
  const totalDwellH = dwellsH ? vias.reduce((s, v) => s + v.dwellH, 0) : 0;

  const advice = useMemo(() => {
    const r = plannedRouteId ? routes.find((x) => x.id === plannedRouteId) : undefined;
    if (!r || r.waypoints.length < 2) return null;
    const w = r.waypoints.map((p) => p.position);
    return recommendDeparture(w, streamTimeMs, streamTimeMs + 12 * 3_600_000, 30, cruiseKn, { dwellsH });
  }, [plannedRouteId, routes, streamTimeMs, cruiseKn, dwellsH]);

  const targetMs = targetArriveMs;

  const arrival = useMemo(() => {
    if (planBy !== 'arrive' || targetMs == null) return null;
    const r = plannedRouteId ? routes.find((x) => x.id === plannedRouteId) : undefined;
    if (!r || r.waypoints.length < 2) return null;
    const w = r.waypoints.map((p) => p.position);
    return recommendForArrival(w, streamTimeMs, targetMs, 30, cruiseKn, targetMs, { dwellsH });
  }, [planBy, targetMs, plannedRouteId, routes, streamTimeMs, cruiseKn, dwellsH]);

  // The departure the per-leg breakdown reflects: the arrival recommendation when
  // we're planning to a target, else a noted slot, else the best / now.
  const breakdown = useMemo(() => {
    const r = plannedRouteId ? routes.find((x) => x.id === plannedRouteId) : undefined;
    if (!r || r.waypoints.length < 2) return null;
    const w = r.waypoints.map((p) => p.position);
    const dep =
      planBy === 'arrive' && arrival?.best
        ? arrival.best.departMs
        : plannedDepartureMs ?? advice?.best.departMs ?? streamTimeMs;
    return { departMs: dep, result: simulatePassage(w, dep, cruiseKn, { dwellsH }) };
  }, [planBy, arrival, plannedRouteId, routes, plannedDepartureMs, advice, streamTimeMs, cruiseKn, dwellsH]);

  const distNm = useMemo(() => {
    const r = plannedRouteId ? routes.find((x) => x.id === plannedRouteId) : undefined;
    if (!r) return 0;
    let d = 0;
    const w = r.waypoints;
    for (let i = 0; i + 1 < w.length; i++) d += rhumbDistanceNm(w[i].position, w[i + 1].position);
    return d;
  }, [plannedRouteId, routes]);

  const route = plannedRouteId ? routes.find((x) => x.id === plannedRouteId) : undefined;
  if (!route) {
    return (
      <StepBox>
        <p className="sub">No route to follow yet. Go Back and plot one first.</p>
      </StepBox>
    );
  }

  function followNow() {
    useRoutingStore.getState().activateRoute(route!.id);
    useShellStore.getState().setMode('Live');
    usePassagePlanStore.getState().finish();
    showToast('Following the route — over in Live');
    closeSheet();
  }

  const useDep =
    planBy === 'arrive' && arrival?.best ? arrival.best.departMs : advice?.best.departMs ?? null;

  return (
    <StepBox>
      <div className="acp-kv">
        <span className="k">Total distance</span>
        <span className="v">{distNm.toFixed(1)} nm</span>
      </div>
      {totalDwellH > 0 && (
        <div className="acp-kv">
          <span className="k">Stops</span>
          <span className="v">
            {vias.filter((v) => v.dwellH > 0).length} · {fmtDur(totalDwellH * 60)} total, in the times below
          </span>
        </div>
      )}

      <label className="acp-fieldlabel" style={{ marginTop: 6 }}>Plan by</label>
      <div className="acp-seg">
        <button type="button" className={planBy === 'depart' ? 'on' : ''} onClick={() => setPlanBy('depart')}>
          Departure
        </button>
        <button type="button" className={planBy === 'arrive' ? 'on' : ''} onClick={() => setPlanBy('arrive')}>
          Arrive by
        </button>
      </div>

      {planBy === 'depart' ? (
        advice ? (
          <>
            <div className="acp-kv">
              <span className="k">Leaving now</span>
              <span className="v">
                {fmtDur(advice.now.durationMin)} · arrive {fmtClock(streamTimeMs + advice.now.durationMin * 60_000)}
                {advice.now.unachievable ? ' · foul tide' : ''}
              </span>
            </div>
            <div className="acp-kv">
              <span className="k">Best departure</span>
              <span className="v">
                {fmtClock(advice.best.departMs)} · {fmtDur(advice.best.durationMin)} · arrive{' '}
                {fmtClock(advice.best.departMs + advice.best.durationMin * 60_000)}
              </span>
            </div>
            {advice.savingMin >= 1 ? (
              <p className="acp-note">
                Leaving at {fmtClock(advice.best.departMs)} saves about {fmtDur(advice.savingMin)} on the
                tide over the next 12 hours. A planning estimate from the chart diamonds, not a guarantee.
              </p>
            ) : (
              <p className="acp-note">
                No real tide saving to be had in the next 12 hours — leaving now is about as good as it gets.
              </p>
            )}
            {plannedDepartureMs != null && (
              <div className="acp-kv">
                <span className="k">Planned departure</span>
                <span className="v">{fmtClock(plannedDepartureMs)}</span>
              </div>
            )}
          </>
        ) : (
          <p className="acp-note">Add at least two waypoints to time the passage.</p>
        )
      ) : (
        <>
          <label className="acp-fieldlabel" style={{ marginTop: 6 }}>Arrive by (UT)</label>
          <input
            type="time"
            className="acp-text-input"
            value={targetMs ? msToHHMM(targetMs) : ''}
            aria-label="Target arrival time"
            onChange={(e) => setTargetArriveMs(hhmmToMs(e.target.value, streamTimeMs))}
          />
          {targetMs != null && (
            <p className="acp-note">
              Target {fmtClock(targetMs)} ·{' '}
              {new Date(targetMs).getUTCDate() === new Date(streamTimeMs).getUTCDate() ? 'today' : 'next day'}
            </p>
          )}
          {arrival == null ? (
            <p className="acp-note">Enter a target arrival time (and plot a route of two or more waypoints).</p>
          ) : arrival.feasible ? (
            <>
              <div className="acp-kv">
                <span className="k">Leave by</span>
                <span className="v">
                  {fmtClock(arrival.best!.departMs)} · {fmtDur(arrival.best!.durationMin)} · arrive{' '}
                  {fmtClock(arrival.best!.arriveMs)}
                </span>
              </div>
              <div className="acp-kv">
                <span className="k">Latest departure</span>
                <span className="v">
                  {fmtClock(arrival.latest!.departMs)} · arrive {fmtClock(arrival.latest!.arriveMs)}
                </span>
              </div>
              <p className="acp-note">
                The recommended departure rides the fairest tide that still makes your target; the latest is
                the last moment you could leave. Estimate from the chart diamonds, check at high zoom.
              </p>
            </>
          ) : (
            <p className="acp-note">
              {arrival.allFoul
                ? 'Foul tide the whole window — the passage can’t hold its track to make that arrival.'
                : arrival.earliest
                  ? `Can’t make ${fmtClock(targetMs!)} — the soonest you’d arrive is ${fmtClock(arrival.earliest.arriveMs)} (leaving ${fmtClock(arrival.earliest.departMs)}).`
                  : 'No departure in the window can meet that target.'}
            </p>
          )}
        </>
      )}

      {breakdown && breakdown.result.legs.length > 0 && (
        <>
          <label className="acp-fieldlabel" style={{ marginTop: 8 }}>
            Legs · departs {fmtClock(breakdown.departMs)}
          </label>
          {breakdown.result.legs.map((lg, i) => (
            <div className="acp-kv" key={i}>
              <span className="k">
                {route!.waypoints[i].name} → {route!.waypoints[i + 1].name}
              </span>
              <span className="v">
                {lg.distanceNm.toFixed(1)} nm · {fmtDur(lg.durationMin)} · {lg.avgSogKn.toFixed(1)} kn
                {lg.unachievable ? ' · foul' : ''}
              </span>
            </div>
          ))}
          <div className="acp-kv">
            <span className="k">Arrival</span>
            <span className="v">{fmtClock(breakdown.departMs + breakdown.result.elapsedMin * 60_000)}</span>
          </div>
        </>
      )}

      <div className="acp-btn-row">
        <button type="button" className="acp-btn primary" onClick={followNow}>
          Follow now
        </button>
        <button
          type="button"
          className="acp-btn"
          disabled={useDep == null}
          onClick={() => {
            if (useDep == null) return;
            useRoutePlanStore.getState().setPlannedDeparture(useDep);
            showToast(`Departure noted: ${fmtClock(useDep)}`);
          }}
        >
          Note departure
        </button>
      </div>
      <p className="acp-note">
        A reminder/alarm for the departure time comes in a later pass; for now we just note it.
      </p>
    </StepBox>
  );
}

function PilotageStep() {
  const plannedRouteId = useRoutePlanStore((s) => s.plannedRouteId);
  const routes = useRoutingStore((s) => s.routes);
  const sections = useRoutePlanStore((s) => s.pilotageSections);
  const showToast = useShellStore((s) => s.showToast);

  const route = plannedRouteId ? routes.find((r) => r.id === plannedRouteId) : undefined;
  if (!route) {
    return (
      <StepBox>
        <p className="sub">No route yet. Go Back and plot one first.</p>
      </StepBox>
    );
  }

  function makePlan(sec: PilotageSection) {
    const title = pilotageSectionTitle(sec);
    usePilotageStore.getState().addPlan(`${title} pilotage`);
    useShellStore.getState().setFocus(sec.position, 14);
    useShellStore.getState().closeSheet();
    showToast(`‘${title} pilotage’ plan created — tap a charted buoy, beacon or light to add a clearing bearing`);
  }

  return (
    <StepBox>
      <p className="sub">
        Stretches that may want closer pilotage. Make a plan for one and the chart jumps there so you
        can lay clearing bearings and transits. A coarse prompt from the charted depths and hazards,
        not the last word.
      </p>
      {sections.length === 0 ? (
        <p className="acp-note">Nothing flagged along the route. Still check the start and finish by eye.</p>
      ) : (
        sections.map((sec, i) => (
          <div className="acp-route-row" key={i}>
            <div className="acp-route-sel" style={{ pointerEvents: 'none' }}>
              <b>{pilotageSectionTitle(sec)}</b>
              <span>{sec.reasons.join(' · ')}</span>
            </div>
            <button type="button" className="acp-btn" onClick={() => makePlan(sec)}>
              Make a plan
            </button>
          </div>
        ))
      )}
      <div className="acp-btn-row">
        <button type="button" className="acp-btn full" onClick={() => useShellStore.getState().openSheet('pilotage')}>
          Open the pilotage plan
        </button>
      </div>
      <p className="acp-note">When you’re done, tap Next to set off.</p>
    </StepBox>
  );
}

/** The guided panel for the current passage-plan step, or null where the bucket carries it. */
export function PlanStepPanel({ stepId }: { stepId: PlanStepId }) {
  if (stepId === 'destination') return <DestinationStep />;
  if (stepId === 'route') return <RouteStep />;
  if (stepId === 'check') return <CheckStep />;
  if (stepId === 'pilotage') return <PilotageStep />;
  if (stepId === 'follow') return <FollowStep />;
  return null;
}
